import React, { useState, useEffect } from 'react';
import { saveLearningSession, auth } from '../services/firebase';

const ExplanationCard = ({ explanations, topic }) => {
  const [selectedStyle, setSelectedStyle] = useState('simple');
  const [saveStatus, setSaveStatus] = useState('');
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    if (explanations && topic && !hasSaved) {
      saveExplanationToDb();
    }
  }, [explanations, topic, hasSaved]);

  const saveExplanationToDb = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      await saveLearningSession(userId, {
        topic,
        explanations,
        selectedStyle,
        type: 'explanation',
        viewed: true,
      });
      
      setHasSaved(true);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving explanation:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleStyleChange = async (styleKey) => {
    setSelectedStyle(styleKey);
    
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        await saveLearningSession(userId, {
          topic,
          selectedStyle: styleKey,
          type: 'style_change',
          timestamp: new Date(),
        });
      }
    } catch (error) {
      console.error('Error tracking style change:', error);
    }
  };

  if (!explanations) return null;

  const styles = [
    { key: 'simple', label: 'Simple', icon: '🎯', color: 'bg-green-500', desc: 'Easy to understand' },
    { key: 'detailed', label: 'Detailed', icon: '📚', color: 'bg-blue-500', desc: 'In-depth explanation' },
    { key: 'analogy', label: 'Analogy', icon: '💡', color: 'bg-purple-500', desc: 'Real-world comparison' },
  ];

  return (
    <div className="card fade-in-up">
      {/* Save Status */}
      {saveStatus === 'saved' && (
        <div className="mb-4 p-2 bg-green-500 bg-opacity-20 border border-green-500 text-green-400 rounded-lg text-sm flex items-center gap-2 fade-in">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Saved to your learning history
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="mb-4 p-2 bg-red-500 bg-opacity-20 border border-red-500 text-red-400 rounded-lg text-sm flex items-center gap-2 fade-in">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Failed to save. Please try again.
        </div>
      )}

      {/* Style Selector */}
      <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
        {styles.map((style) => (
          <button
            key={style.key}
            onClick={() => handleStyleChange(style.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap hover-lift ${
              selectedStyle === style.key
                ? `${style.color} text-white shadow-lg scale-105`
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            <span className="text-lg">{style.icon}</span>
            <div className="text-left">
              <div className="font-semibold">{style.label}</div>
              <div className="text-xs opacity-80">{style.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Explanation Content */}
      <div className="prose prose-invert max-w-none">
        <div className="bg-slate-700 rounded-lg p-6 fade-in" key={selectedStyle}>
          <div className="flex items-start gap-3 mb-3">
            <span className="text-3xl">{styles.find(s => s.key === selectedStyle)?.icon}</span>
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">
                {styles.find(s => s.key === selectedStyle)?.label} Explanation
              </h3>
              <p className="text-sm text-slate-400">
                {styles.find(s => s.key === selectedStyle)?.desc}
              </p>
            </div>
          </div>
          <p className="text-slate-100 leading-relaxed whitespace-pre-wrap text-base">
            {explanations[selectedStyle]}
          </p>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 bg-indigo-500 bg-opacity-10 border border-indigo-500 rounded-lg hover-glow">
        <p className="text-sm text-indigo-300">
          💡 <strong>Pro Tip:</strong> Try switching between explanation styles to find the one that clicks best with your learning style!
        </p>
      </div>
    </div>
  );
};

export default ExplanationCard;